/**
 * A coleção de endpoints que a clínica usa para ligar a VecchIA a outro
 * sistema.
 *
 * **É catálogo escrito à mão, e é de propósito.** O PostgREST publica um
 * OpenAPI com 82 caminhos — toda tabela, todo filtro, toda função. Entregar
 * aquilo cru seria entregar o esquema do banco no lugar de uma API: ninguém
 * descobre por onde começar, e cada coluna nova vira promessa de contrato.
 * Aqui ficam os poucos caminhos que respondem às perguntas reais de quem
 * integra, com exemplo pronto.
 *
 * O que evita a documentação envelhecer é a conferência: a tela compara este
 * catálogo com o OpenAPI do servidor e avisa quando um caminho deixa de
 * existir. Documentação que mente é pior que documentação que falta.
 */

export type CategoriaDaApi = 'autenticacao' | 'contatos' | 'agenda' | 'atendimentos' | 'financeiro' | 'catalogo';

export interface EndpointDaApi {
    id: string;
    categoria: CategoriaDaApi;
    metodo: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    /** Caminho a partir da base, já com os parâmetros de exemplo. */
    caminho: string;
    /** O caminho sem query, como o OpenAPI o publica — é por ele que se confere. */
    caminhoCru: string;
    titulo: string;
    descricao: string;
    /** Onde vive: o PostgREST, o GoTrue ou a borda. */
    servico: 'rest' | 'auth' | 'functions';
    corpo?: Record<string, unknown>;
    /** Recorte do que volta, para a pessoa saber o formato sem chamar. */
    resposta?: unknown;
    /** O que costuma dar errado com este endpoint. */
    atencao?: string;
}

export const CATEGORIAS_DA_API: { id: CategoriaDaApi; rotulo: string; descricao: string }[] = [
    { id: 'autenticacao', rotulo: 'Autenticação', descricao: 'Como obter e renovar o token que todas as outras chamadas exigem' },
    { id: 'contatos', rotulo: 'Contatos e pacientes', descricao: 'Pessoas da clínica: cadastro, pacientes e leads' },
    { id: 'agenda', rotulo: 'Agenda', descricao: 'Agendamentos, salas e horários de funcionamento' },
    { id: 'atendimentos', rotulo: 'Atendimentos', descricao: 'Consultas realizadas e fichas preenchidas' },
    { id: 'financeiro', rotulo: 'Financeiro', descricao: 'Títulos a receber e a pagar, e pagamentos' },
    { id: 'catalogo', rotulo: 'Catálogo e estoque', descricao: 'Procedimentos, produtos e saldo' },
];

export const ENDPOINTS_DA_API: EndpointDaApi[] = [
    {
        id: 'login',
        categoria: 'autenticacao',
        metodo: 'POST',
        servico: 'auth',
        caminho: '/auth/v1/token?grant_type=password',
        caminhoCru: '/auth/v1/token',
        titulo: 'Entrar e obter o token',
        descricao: 'Devolve `access_token` (vale 1 hora) e `refresh_token`. Todas as outras chamadas mandam o `access_token` no cabeçalho `Authorization`.',
        corpo: { email: 'integracao@clinicavecchia.com.br', password: 'a senha desta conta' },
        resposta: { access_token: 'eyJhbGciOi...', token_type: 'bearer', expires_in: 3600, refresh_token: 'v1.M2Rk...' },
        atencao: 'A integração usa uma conta de pessoa, com as permissões dela. Não existe ainda chave de API por clínica — o que essa conta não pode ver, a integração também não vê.',
    },
    {
        id: 'refresh',
        categoria: 'autenticacao',
        metodo: 'POST',
        servico: 'auth',
        caminho: '/auth/v1/token?grant_type=refresh_token',
        caminhoCru: '/auth/v1/token',
        titulo: 'Renovar o token',
        descricao: 'Troca o `refresh_token` por um `access_token` novo, sem pedir a senha de novo.',
        corpo: { refresh_token: 'v1.M2Rk...' },
    },
    {
        id: 'contatos-listar',
        categoria: 'contatos',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/contatos?select=id,nome,telefone,email&order=nome',
        caminhoCru: '/contatos',
        titulo: 'Listar contatos',
        descricao: 'A pessoa é uma só: paciente e lead são papéis dela. `select` escolhe as colunas; `order`, `limit` e `offset` funcionam como em qualquer coleção.',
        resposta: [{ id: '11111111-…', nome: 'Clara Ribeiro', telefone: '(11) 99999-9999', email: 'clara@exemplo.com' }],
        atencao: 'A RLS recorta pela clínica de quem está no token. Não é preciso — nem possível — filtrar por `clinica_id`.',
    },
    {
        id: 'contatos-criar',
        categoria: 'contatos',
        metodo: 'POST',
        servico: 'rest',
        caminho: '/rest/v1/contatos',
        caminhoCru: '/contatos',
        titulo: 'Criar contato',
        descricao: 'Mande `Prefer: return=representation` para receber a linha criada de volta.',
        corpo: { clinica_id: 'o id da sua clínica', nome: 'Marina Duarte', telefone: '(11) 98812-2210' },
        atencao: '`clinica_id` é obrigatório e precisa ser o da sua clínica: a RLS recusa qualquer outro.',
    },
    {
        id: 'pacientes',
        categoria: 'contatos',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/pacientes?select=contato_id,nascimento,situacao,contatos(nome,telefone)',
        caminhoCru: '/pacientes',
        titulo: 'Listar pacientes com o contato embutido',
        descricao: 'O embutido `contatos(...)` traz a pessoa junto, numa chamada só.',
        atencao: 'Quando a chave estrangeira é única, o embutido volta como OBJETO, não como lista.',
    },
    {
        id: 'agendamentos-listar',
        categoria: 'agenda',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/agendamentos?select=id,inicio_em,duracao_min,status,procedimentos(nome),contatos(nome)&inicio_em=gte.2026-09-01&order=inicio_em',
        caminhoCru: '/agendamentos',
        titulo: 'Listar agendamentos de um período',
        descricao: 'Filtros de data usam os operadores do PostgREST: `gte.`, `lte.`, `eq.`.',
        resposta: [{ id: '…', inicio_em: '2026-09-08T08:00:00-03:00', duracao_min: 30, status: 'concluido' }],
    },
    {
        id: 'agendamentos-criar',
        categoria: 'agenda',
        metodo: 'POST',
        servico: 'rest',
        caminho: '/rest/v1/agendamentos',
        caminhoCru: '/agendamentos',
        titulo: 'Criar agendamento',
        descricao: '`fim_em` é calculado por gatilho a partir de `inicio_em` e `duracao_min` — não mande.',
        corpo: {
            clinica_id: 'o id da sua clínica',
            contato_id: 'id do paciente',
            procedimento_id: 'id do procedimento',
            profissional_id: 'id do membro',
            inicio_em: '2026-09-20T14:00:00-03:00',
            duracao_min: 60,
            status: 'confirmado',
        },
        atencao: 'Status válidos: confirmado, pendente, concluido, cancelado, faltou, bloqueio. Bloqueio exige `titulo` e não aceita `contato_id`.',
    },
    {
        id: 'jornada',
        categoria: 'agenda',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/horarios_funcionamento?select=dia_semana,inicio,fim&order=dia_semana',
        caminhoCru: '/horarios_funcionamento',
        titulo: 'Ler a jornada da clínica',
        descricao: 'Uma linha por faixa. Dia sem linha é dia fechado — é assim que se sabe quando a clínica abre.',
    },
    {
        id: 'atendimentos',
        categoria: 'atendimentos',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/atendimentos?select=id,iniciado_em,encerrado_em,situacao,valor_centavos&situacao=eq.concluido',
        caminhoCru: '/atendimentos',
        titulo: 'Listar atendimentos',
        descricao: 'O atendimento é a consulta que aconteceu; o agendamento é a intenção. `valor_centavos` é inteiro em centavos.',
    },
    {
        id: 'fichas',
        categoria: 'atendimentos',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/fichas_preenchidas?select=id,nome,respostas,preenchida_em&atendimento_id=eq.SEU_ID',
        caminhoCru: '/fichas_preenchidas',
        titulo: 'Ler as fichas de um atendimento',
        descricao: 'Cada ficha guarda a definição do modelo com que foi preenchida: ler pelo modelo de hoje reescreveria o prontuário de ontem.',
        atencao: 'Prontuário é dado sensível. Ler ficha é evento de auditoria — e a leitura pela API também é sua responsabilidade.',
    },
    {
        id: 'titulos',
        categoria: 'financeiro',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/titulos?select=id,descricao,tipo,valor_centavos,vencimento,situacao&situacao=neq.pago',
        caminhoCru: '/titulos',
        titulo: 'Listar títulos em aberto',
        descricao: 'Dinheiro é sempre inteiro em centavos. `tipo` é `receber` ou `pagar`.',
        atencao: '`situacao` vem de gatilho, a partir dos pagamentos: não adianta escrevê-la.',
    },
    {
        id: 'pagamentos',
        categoria: 'financeiro',
        metodo: 'POST',
        servico: 'rest',
        caminho: '/rest/v1/pagamentos',
        caminhoCru: '/pagamentos',
        titulo: 'Registrar um pagamento',
        descricao: 'A baixa do título é consequência: o gatilho recalcula `situacao` e o valor pago.',
        corpo: {
            clinica_id: 'o id da sua clínica',
            titulo_id: 'id do título',
            valor_centavos: 21000,
            taxa_centavos: 0,
            pago_em: '2026-09-20',
        },
    },
    {
        id: 'procedimentos',
        categoria: 'catalogo',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/procedimentos?select=id,nome,categoria,duracao_min,preco_centavos&ativo=is.true',
        caminhoCru: '/procedimentos',
        titulo: 'Listar procedimentos',
        descricao: 'O catálogo que a agenda e o orçamento usam.',
    },
    {
        id: 'produtos',
        categoria: 'catalogo',
        metodo: 'GET',
        servico: 'rest',
        caminho: '/rest/v1/produtos?select=id,nome,quantidade,minimo,unidade&ativo=is.true',
        caminhoCru: '/produtos',
        titulo: 'Listar produtos e saldo',
        descricao: '`quantidade` é mantida por gatilho a partir das movimentações — escrever nela não move estoque.',
        atencao: 'Para dar entrada ou saída, crie uma linha em `movimentacoes_estoque`.',
    },
    {
        id: 'movimentacao',
        categoria: 'catalogo',
        metodo: 'POST',
        servico: 'rest',
        caminho: '/rest/v1/movimentacoes_estoque',
        caminhoCru: '/movimentacoes_estoque',
        titulo: 'Movimentar estoque',
        descricao: 'Entrada, saída ou ajuste. O saldo do produto é recalculado pelo gatilho.',
        corpo: {
            clinica_id: 'o id da sua clínica',
            produto_id: 'id do produto',
            tipo: 'entrada',
            quantidade: 12,
            origem: 'Compra NF 1234',
        },
    },
];

/** O `curl` pronto, com a base da instalação de quem está lendo. */
export function curlDoEndpoint(endpoint: EndpointDaApi, base: string, chaveAnon: string): string {
    const linhas = [`curl -X ${endpoint.metodo} '${base}${endpoint.caminho}'`];
    if (endpoint.servico !== 'functions') linhas.push(`  -H 'apikey: ${chaveAnon}'`);
    if (endpoint.servico !== 'auth') linhas.push("  -H 'Authorization: Bearer SEU_ACCESS_TOKEN'");
    else linhas.push(`  -H 'apikey: ${chaveAnon}'`);
    if (endpoint.corpo) {
        linhas.push("  -H 'Content-Type: application/json'");
        if (endpoint.metodo === 'POST' && endpoint.servico === 'rest') {
            linhas.push("  -H 'Prefer: return=representation'");
        }
        linhas.push(`  -d '${JSON.stringify(endpoint.corpo)}'`);
    }
    return linhas.join(' \\\n');
}
